class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest(".search-modal");
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.searchTerm = "";

    this._lastInputTs = 0;
    this._isTyping = false;
    this._typingTimer = null;

    this._primedOpen = false;
    this._firstResults = true;

    this._firstSearchGuardUntil = 0;

    this._submitBtn = null;
    this._submitPrevDisabled = null;

    this._focusGuardActive = false;
    this._focusInCapture = this._focusInCapture.bind(this);

    this.setupEventListeners();

    this._installSyncOpenFocus();
  }

  setupEventListeners() {
    this.querySelector("form.search").addEventListener("submit", this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener("click", this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener("click", this.clear.bind(this));

    let debounceTimer;
    this.input.addEventListener("input", (event) => {
      clearTimeout(debounceTimer);

      const prevValue = this.searchTerm || "";
      const currValue = this.input.value || "";

      this._lastInputTs = Date.now();
      this._isTyping = true;
      if (this._typingTimer) clearTimeout(this._typingTimer);
      this._typingTimer = setTimeout(() => (this._isTyping = false), 500);

      if (!prevValue.trim() && currValue.trim().length === 1) {
        this._firstSearchGuardUntil = Date.now() + 3500;
        this._activateFocusGuard();
      } else if (this._firstSearchGuardUntil && currValue.trim().length >= 2) {
        this._firstSearchGuardUntil = 0;
        this._deactivateFocusGuard();
      }

      const wasFocused = this._wasFocused || document.activeElement === this.input;

      debounceTimer = setTimeout(() => {
        this.onChange(event);

        if (wasFocused || ("ontouchstart" in window && this._isTyping)) {
          const restoreFocus = () => {
            if (document.activeElement !== this.input) {
              try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
            }
          };
          setTimeout(restoreFocus, 50);
        }
      }, 200);
    });

    this.input.addEventListener("focus", () => { this._wasFocused = true; });

    this.input.addEventListener("blur", () => {
      this._wasFocused = false;
      const now = Date.now();
      const withinShortGuard = now - (this._lastInputTs || 0) < 800;
      const withinFirstSearchGuard = now < (this._firstSearchGuardUntil || 0);

      if (withinShortGuard || withinFirstSearchGuard) {
        setTimeout(() => {
          const ae = document.activeElement;
          const focusedInsideResults =
            this.predictiveSearchResults && this.predictiveSearchResults.contains(ae);
          if (!focusedInsideResults) {
            try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
          }
        }, 0);
      }
    });

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    this.addEventListener("click", (e) => e.stopPropagation());
    this.input.addEventListener("click", (e) => e.stopPropagation());

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener("pointerdown", (e) => {
        const t = e.target;
        const interactive = t.closest("a,button,[role='option'],[tabindex]");
        if (!interactive) e.preventDefault();
      });
    }

    this._observeModalOpen();
  }

  _focusInCapture(e) {
    if (!this._focusGuardActive) return;

    const t = e.target;

    if (t === this.input) return;

    const inResults = this.predictiveSearchResults && this.predictiveSearchResults.contains(t);
    const isInteractiveResult = inResults && t.closest("a,button,[role='option'],[tabindex]");
    if (isInteractiveResult) return;

    const trapTarget = t.closest(
      "button[type='submit'], summary, .header__icon, .modal_toggle, [data-modal-toggle]"
    );
    if (trapTarget) {
      e.stopImmediatePropagation();
      e.preventDefault();
      queueMicrotask(() => {
        try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
      });
    }
  }

  _activateFocusGuard() {
    if (this._focusGuardActive) {
      this._firstSearchGuardUntil = Date.now() + 3500;
      return;
    }
    this._focusGuardActive = true;
    document.addEventListener("focusin", this._focusInCapture, true);
    setTimeout(() => this._deactivateFocusGuard(), 3600);
  }

  _deactivateFocusGuard() {
    if (!this._focusGuardActive) return;
    this._focusGuardActive = false;
    document.removeEventListener("focusin", this._focusInCapture, true);
  }

  _demoteSubmitTemporarily() {
    if (!this._submitBtn) this._submitBtn = this.querySelector('button[type="submit"]');
    if (!this._submitBtn || this._submitBtn.__psDemoted) return;

    this._submitPrevDisabled = this._submitBtn.disabled;
    this._submitBtn.disabled = true;
    this._submitBtn.__psDemoted = true;

    setTimeout(() => this._restoreSubmit(), 3500);
  }

  _restoreSubmit() {
    if (!this._submitBtn) return;
    if (this._submitPrevDisabled == null) this._submitBtn.disabled = false;
    else this._submitBtn.disabled = this._submitPrevDisabled;
    this._submitBtn.__psDemoted = false;
  }

  _modalIsOpen() {
    if (!this.modal) return false;
    const c = this.modal.classList;
    if (
      this.modal.hasAttribute("open") ||
      c.contains("open") ||
      c.contains("is-open") ||
      c.contains("active") ||
      c.contains("visible")
    ) {
      return true;
    }
    const cs = getComputedStyle(this.modal);
    return cs.display !== "none" && cs.visibility !== "hidden" && parseFloat(cs.opacity || "1") > 0.01;
  }

  _observeModalOpen() {
    if (!this.modal || !this.input) return;

    const focusSafely = () => {
      [0, 60, 180].forEach((ms) => {
        setTimeout(() => {
          if (document.activeElement !== this.input) {
            try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
          }
        }, ms);
      });
    };

    const maybeFocusOnOpen = () => {
      if (this._modalIsOpen() && document.activeElement !== this.input) {
        this._demoteSubmitTemporarily();
        focusSafely();
      }
    };

    maybeFocusOnOpen();

    this._modalObserver = new MutationObserver(maybeFocusOnOpen);
    this._modalObserver.observe(this.modal, {
      attributes: true,
      attributeFilter: ["open", "class", "style", "aria-hidden"],
    });

    this.modal.addEventListener("transitionend", maybeFocusOnOpen, true);
    this.modal.addEventListener("animationend", maybeFocusOnOpen, true);
  }

  _installSyncOpenFocus() {
    if (!this.modal || !this.input) return;

    const openerSelectorList = [
      '[data-open-search]',
      '[data-action="open-search"]',
      '[data-search-open]',
      '.js-open-search',
      '.header__search',
      this.modal.id ? `button[aria-controls="${this.modal.id}"]` : null,
      this.modal.id ? `a[aria-controls="${this.modal.id}"]` : null,
    ].filter(Boolean);

    const isOpener = (el) => !!openerSelectorList.find(sel => el.closest(sel));

    const makeTemporarilyVisibleForFocus = () => {
      const cs = getComputedStyle(this.modal);
      const tweaks = {};

      if (cs.display === 'none') { tweaks.display = this.modal.style.display; this.modal.style.display = 'block'; }
      if (cs.visibility === 'hidden') { tweaks.visibility = this.modal.style.visibility; this.modal.style.visibility = 'visible'; }
      if (parseFloat(cs.opacity || '1') < 0.01) { tweaks.opacity = this.modal.style.opacity; this.modal.style.opacity = '1'; }
      if (cs.pointerEvents === 'none') { tweaks.pointerEvents = this.modal.style.pointerEvents; this.modal.style.pointerEvents = 'auto'; }

      const hadHiddenAttr = this.modal.hasAttribute('hidden');
      const prevAriaHidden = this.modal.getAttribute('aria-hidden');
      if (hadHiddenAttr) this.modal.removeAttribute('hidden');
      if (prevAriaHidden === 'true') this.modal.setAttribute('aria-hidden', 'false');

      const cleanup = () => {
        if ('display' in tweaks) this.modal.style.display = tweaks.display;
        if ('visibility' in tweaks) this.modal.style.visibility = tweaks.visibility;
        if ('opacity' in tweaks) this.modal.style.opacity = tweaks.opacity;
        if ('pointerEvents' in tweaks) this.modal.style.pointerEvents = tweaks.pointerEvents;
        if (hadHiddenAttr) this.modal.setAttribute('hidden', '');
        if (prevAriaHidden === 'true') this.modal.setAttribute('aria-hidden', 'true');
      };

      return cleanup;
    };

    const focusSynchronously = () => {
      this._demoteSubmitTemporarily();
      try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
    };

    const handler = (e) => {
      const t = e.target;
      if (!t || !isOpener(t)) return;

      const cleanup = makeTemporarilyVisibleForFocus();
      focusSynchronously();

      requestAnimationFrame(() => cleanup());
    };

    document.addEventListener('click', handler, { capture: true });
    document.addEventListener('touchend', handler, { capture: true, passive: false });
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    const newSearchTerm = this.getQuery();
    this._lastInputTs = Date.now();

    if (!newSearchTerm.length) {
      this.clear();
      this.close(true);
      this._firstSearchGuardUntil = 0;
      this._deactivateFocusGuard();
      return;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    if (this.getQuery().length) {
      this.input.value = this.getQuery();
    }
  }

  onFocus() {
    document.body.classList.add("predictive-search--focus");
    const currentSearchTerm = this.getQuery();

    if (!this._primedOpen) {
      this._primedOpen = true;

      this._demoteSubmitTemporarily();

      if ("visualViewport" in window) {
        requestAnimationFrame(() => {
          const headerBottom = document.querySelector(".header")?.getBoundingClientRect().bottom || 0;
          this.resultsMaxHeight = Math.floor(window.visualViewport.height - headerBottom);
          this.open();
        });
      } else {
        this.resultsMaxHeight = this.getResultsMaxHeight();
        this.open();
      }
    } else if (!this.resultsMaxHeight) {
      if ("visualViewport" in window) {
        requestAnimationFrame(() => {
          const headerBottom = document.querySelector(".header")?.getBoundingClientRect().bottom || 0;
          this.resultsMaxHeight = Math.floor(window.visualViewport.height - headerBottom);
        });
      } else {
        this.resultsMaxHeight = this.getResultsMaxHeight();
      }
    }

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const now = Date.now();
      const withinShortGuard = now - (this._lastInputTs || 0) < 800;
      const withinFirstSearchGuard = now < (this._firstSearchGuardUntil || 0);
      if (withinShortGuard || withinFirstSearchGuard) return;
      if (!this.contains(document.activeElement)) this.close();
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear();
      this.close(true);
      return;
    }
    switch (event.code) {
      case "ArrowUp": this.switchOption("up"); break;
      case "ArrowDown": this.switchOption("down"); break;
      case "Enter": this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector("[data-predictive-search-search-for-text]");
    const current = el?.innerText;
    if (current) el.innerText = current.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;
    const moveUp = direction === "up";
    const selected = this.querySelector('[aria-selected="true"]');
    const all = this.querySelectorAll("li");
    let active = this.querySelector("li");
    if (moveUp && !selected) return;

    this.statusElement.textContent = "";

    if (!moveUp && selected) active = selected.nextElementSibling || all[0];
    else if (moveUp) active = selected.previousElementSibling || all[all.length - 1];

    if (active === selected) return;
    active.setAttribute("aria-selected", true);
    if (selected) selected.setAttribute("aria-selected", false);

    this.setLiveRegionText(active.textContent);
    this.input.setAttribute("aria-activedescendant", active.id);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal && !this._firstResults) this.modal.classList.add("searching");
      return;
    }

    fetch(
      `${theme.routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&${encodeURIComponent("resources[limit]")}=${perPage}&${encodeURIComponent(
        "resources[limit_scope]"
      )}=each&${encodeURIComponent("resources[type]")}=product,collection&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);

        if (this.modal) {
          if (this._firstResults) {
            setTimeout(() => { this.modal.classList.add("searching"); }, 300);
            this._firstResults = false;
          } else {
            this.modal.classList.add("searching");
          }
        }
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;
    setTimeout(() => { this.statusElement.setAttribute("aria-hidden", "true"); }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const wasFocused = this._wasFocused || document.activeElement === this.input;
    const isMobile = "ontouchstart" in window;

    requestAnimationFrame(() => {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      requestAnimationFrame(() => {
        this.setAttribute("results", true);
        this.setLiveRegionResults();

        this.open();

        if ((wasFocused || (isMobile && this._isTyping)) && document.activeElement !== this.input) {
          setTimeout(() => {
            try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }
          }, 80);
        }
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      (document.querySelector(".header")?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.resultsMaxHeight) {
      this.resultsMaxHeight = this.getResultsMaxHeight();
    }
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    document.body.classList.add("predictive-search--focus");
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    document.body.classList.remove("predictive-search--focus");
  }

  clear(event) {
    if (event) event.preventDefault();

    this.input.value = "";
    this.searchTerm = "";
    this.removeAttribute("open");
    this.removeAttribute("results");

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = "";
    }

    this.cachedResults = {};

    try { this.input.focus({ preventScroll: true }); } catch { this.input.focus(); }

    if (this.modal) this.modal.classList.remove("searching");
  }
}

customElements.define("predictive-search", PredictiveSearch);
